import { useMeasure } from '@jouzen/ecom-components/hooks/useMeasure';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { type RefObject, useId, useRef } from 'react';

import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import type { PopAccuracyComfortSectionHighlightData } from '../types';

export interface DesktopHighlightsAccordionProps {
  readonly activeIndex: number;
  readonly highlight: PopAccuracyComfortSectionHighlightData;
  readonly index: number;
  readonly onChange: (index: number) => void;
}

const DesktopHighlightsAccordion = ({
  activeIndex,
  highlight,
  index,
  onChange,
}: DesktopHighlightsAccordionProps) => {
  const isActive = activeIndex === index;
  const t = useTranslations();
  const a11yId = useId();
  const titleId = `highlights-accordion-title-${a11yId}`;
  const copyId = `highlights-accordion-copy-${a11yId}`;
  const copyRef = useRef<HTMLDivElement>(null);
  const { height: copyHeight } = useMeasure({
    ref: copyRef as RefObject<HTMLDivElement>,
  });

  const handleClick = () => {
    if (!isActive) {
      onChange(index);
    }
  };

  return (
    <div>
      <Typography className="text-current" Element="h3">
        <button
          aria-controls={copyId}
          aria-expanded={isActive}
          className="relative block pl-5 text-2xl text-current"
          data-cy="desktop-accordion-toggle"
          id={titleId}
          onClick={handleClick}
          type="button"
        >
          <div
            aria-hidden="true"
            className={cx(
              'absolute top-0 bottom-0 left-0 w-px bg-current transition-opacity duration-300',
              {
                'opacity-30': !isActive,
              },
            )}
          />
          <span
            className={cx('transition-opacity duration-300', {
              'opacity-75': !isActive,
            })}
          >
            {t(highlight.title)}
          </span>
        </button>
      </Typography>
      <div
        aria-hidden={!isActive}
        aria-labelledby={titleId}
        data-cy="desktop-accordion-content"
        id={copyId}
        role="region"
      >
        <Motion
          animate={{
            height: isActive ? copyHeight : 0,
            opacity: isActive ? 1 : 0,
          }}
          className="text-clip"
          transition={{ duration: 0.35, type: 'tween' }}
        >
          <div ref={copyRef} className="relative pt-1 pl-5">
            <div
              aria-hidden="true"
              className="absolute inset-y-0 left-0 w-px bg-current opacity-30"
            />
            <Typography className="max-w-72 text-sm text-current">
              <RichText messageKey={highlight.copy} />
            </Typography>
          </div>
        </Motion>
      </div>
    </div>
  );
};

export default DesktopHighlightsAccordion;
